import { Input } from "@legal-500/legal500-design-system";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { nextItemId } from "./blockContent";
import { BlockItemList, RichTextField } from "./blockEditorParts";
import { withoutEmptySection } from "./landingEditorParts";
import { SectionFields, type SectionFormValues, toSectionFormValues } from "./SectionFields";
import type { AccordionContent, AccordionItem, BlockContentErrors } from "./types";
import { useContentChange } from "./useContentChange";
import styles from "../CMS.module.css";

type AccordionEditorProps = {
  value: AccordionContent;
  onChange: (value: AccordionContent) => void;
  errors: BlockContentErrors;
  showBackground: boolean;
};

type AccordionFormValues = SectionFormValues & {
  items: AccordionItem[];
};

function toContent(initial: AccordionContent, values: AccordionFormValues): AccordionContent {
  return withoutEmptySection({
    ...initial,
    ...values,
    items: values.items.map((item, index) => ({
      ...item,
      order: index,
      question: item.question.trim(),
    })),
  });
}

export function AccordionEditor({ value, onChange, errors, showBackground }: AccordionEditorProps) {
  const [initial] = useState(value);
  const { control, register, watch, getValues } = useForm<AccordionFormValues>({
    defaultValues: { ...toSectionFormValues(value), items: value.items },
  });
  const { fields, append, remove, move } = useFieldArray<AccordionFormValues, "items", "fieldKey">({
    control,
    name: "items",
    keyName: "fieldKey",
  });
  useContentChange(watch, onChange, values => toContent(initial, values));

  return (
    <div className={styles.blockEditor}>
      <SectionFields
        register={register}
        idPrefix="accordion"
        errors={errors}
        showBackground={showBackground}
      />

      <BlockItemList
        noun="Question"
        keys={fields.map(field => field.fieldKey)}
        onMove={(from, to) => move(from, to)}
        onRemove={remove}
        onAdd={() => {
          const items = getValues("items");
          append({ id: nextItemId(items), order: items.length, question: "", answer: "" });
        }}
        renderItem={(index, key) => (
          <>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor={`accordion-question-${key}`}>
                Question {index + 1} <span className={styles.required}>*</span>
              </label>
              <Input
                id={`accordion-question-${key}`}
                error={errors[`items.${index}.question`]}
                {...register(`items.${index}.question`)}
              />
            </div>
            <RichTextField
              control={control}
              name={`items.${index}.answer`}
              id={`accordion-answer-${key}`}
              label={`Answer to question ${index + 1}`}
              minHeight={120}
              emptyAsBlank
            />
          </>
        )}
      />
    </div>
  );
}
